import {
  FetchVendureProductsParams,
  VendureFacet,
  VendureProduct,
  VendureProductDetail,
} from "@/lib/vendure/models";
import { VENDURE_API_URL } from "@/environments";
import { defaultChannel } from "@/utils/default-channel";

export async function fetchProductAddons(
  productId: string,
  channel: string | null
): Promise<any[]> {
  if (!productId) {
    throw new Error("fetchProductAddons: productId is missing");
  }

  const query = `
    query GetProductAddons($productId: ID!) {
      product(id: $productId) {
        id
        addons {
          id
          price
          sortOrder
          condition
          addonProduct {
            id
            name
            featuredAsset {
              preview
            }
          }
        }
      }
    }
  `;

  const response = await fetch(VENDURE_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vendure-token": channel || defaultChannel,
    },
    body: JSON.stringify({
      query,
      variables: { productId },
    }),
    cache: "no-store",
  });

  const result = await response.json();

  if (result.errors) {
    console.error("Vendure GraphQL errors:", result.errors);
    throw new Error("Failed to fetch product addons");
  }

  return result.data?.product?.addons ?? [];
}

export async function fetchVendureFacets(
  channel: string | null
): Promise<VendureFacet[]> {
  const query = `
    query GetFacets {
      facets(options: { take: 100 }) {
        items {
          id
          name
          code
          values {
            id
            name
            code
          }
        }
        totalItems
      }
    }
  `;

  const response = await fetch(VENDURE_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vendure-token": channel ?? defaultChannel,
    },
    body: JSON.stringify({ query }),
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`Vendure API error: ${response.statusText}`);
  }

  const result = await response.json();

  if (result.errors) {
    console.error(result.errors);
    throw new Error("Failed to fetch facets from Vendure");
  }

  return result.data.facets.items;
}

export async function fetchVendureAllFacetCounts(
  channel: string | null
): Promise<Record<string, Record<string, number>>> {
  const query = `
    query GetFacetCounts {
      search(input: {
        groupByProduct: true
      }) {
        facetValues {
          facetValue {
            code
            facet {
              id
            }
          }
          count
        }
      }
    }
  `;

  const response = await fetch(VENDURE_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vendure-token": channel ?? defaultChannel,
    },
    body: JSON.stringify({ query }),
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`Vendure API error: ${response.statusText}`);
  }

  const result = await response.json();

  if (result.errors) {
    console.error(result.errors);
    throw new Error("Failed to fetch facet counts");
  }

  const map: Record<string, Record<string, number>> = {};

  for (const fv of result.data.search.facetValues) {
    const facetId = fv.facetValue.facet.id;
    const valueCode = fv.facetValue.code;

    if (!map[facetId]) {
      map[facetId] = {};
    }

    map[facetId][valueCode] = fv.count;
  }

  return map;
}

export type UrlFacetParams = Record<string, string>;

export async function fetchVendureProducts({
  take = 20,
  skip = 0,
  channel,
  facets = [],
  params = {},
}: {
  take?: number;
  skip?: number;
  channel: string;
  facets: {
    id: string;
    code: string;
    values: { id: string; code: string }[];
  }[];
  params: UrlFacetParams;
}): Promise<{
  items: VendureProduct[];
  totalItems: number;
}> {
  const facetValueIdMap: Record<
    string,
    Record<string, string>
  > = Object.fromEntries(
    facets.map((facet) => [
      facet.code,
      Object.fromEntries(facet.values.map((v) => [v.code, String(v.id)])),
    ])
  );

  const facetValueFilters = Object.entries(params)
    .filter(([facetCode]) => facetValueIdMap[facetCode])
    .map(([facetCode, valueCodes]) => {
      const ids = valueCodes
        .split(",")
        .map((valueCode) => facetValueIdMap[facetCode]?.[valueCode])
        .filter(Boolean);

      if (!ids.length) return null;

      return ids.length === 1 ? { and: ids[0] } : { or: ids };
    })
    .filter(Boolean);

  const query = `
    query GetProducts(
      $take: Int!
      $skip: Int!
      $facetValueFilters: [FacetValueFilterInput!]
    ) {
      search(input: {
        take: $take
        skip: $skip
        groupByProduct: true
        facetValueFilters: $facetValueFilters
      }) {
        items {
          productId
          productName
          slug
          description
          productAsset { id preview }
          price {
            ... on PriceRange { min max }
          }
          priceWithTax {
            ... on PriceRange { min max }
          }
          currencyCode
        }
        totalItems
      }
    }
  `;

  const response = await fetch(VENDURE_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vendure-token": channel,
    },
    body: JSON.stringify({
      query,
      variables: {
        take,
        skip,
        facetValueFilters: facetValueFilters.length
          ? facetValueFilters
          : undefined,
      },
    }),
    cache: "no-store",
  });

  const result = await response.json();

  if (result.errors) {
    console.error("Vendure GraphQL errors:", result.errors);
    console.error("facetValueFilters sent:", facetValueFilters);
    throw new Error("Failed to fetch products from Vendure");
  }

  return {
    items: result.data.search.items,
    totalItems: result.data.search.totalItems,
  };
}

export async function fetchVendureHighestPrice(
  channel: string | null
): Promise<number> {
  const query = `
    query HighestPrice {
      search(input: {
        take: 1
        sort: { price: DESC }
        groupByProduct: true
      }) {
        items {
          priceWithTax {
            ... on SinglePrice {
              value
            }
            ... on PriceRange {
              max
            }
          }
        }
      }
    }
  `;

  const response = await fetch(VENDURE_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vendure-token": channel || defaultChannel,
    },
    body: JSON.stringify({
      query,
    }),
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`Vendure API error: ${response.statusText}`);
  }

  const result = await response.json();

  if (result.errors) {
    console.error(result.errors);
    throw new Error("Failed to fetch product from Vendure");
  }

  return result.data.search.items[0]?.priceWithTax?.max / 100;
}

export async function searchVendureProducts(
  searchTerm: string,
  channel: string | null,
  take: number = 10
): Promise<VendureProduct[]> {
  const query = `
    query SearchProducts($term: String!, $take: Int!) {
      search(input: {
        term: $term
        take: $take
        groupByProduct: true
      }) {
        items {
          productId
          productName
          slug
          description
          productAsset {
            id
            preview
          }
          price {
            ... on PriceRange {
              min
              max
            }
          }
          priceWithTax {
            ... on PriceRange {
              min
              max
            }
          }
          currencyCode
        }
      }
    }
  `;

  try {
    const response = await fetch(VENDURE_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "vendure-token": channel || defaultChannel,
      },
      body: JSON.stringify({
        query,
        variables: {
          term: searchTerm,
          take,
        },
      }),
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error(`Vendure API error: ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
      console.error("GraphQL errors:", result.errors);
      throw new Error("Failed to search products from Vendure");
    }

    return result.data?.search?.items || [];
  } catch (error) {
    console.error("Error searching Vendure products:", error);
    throw error;
  }
}

export async function fetchVendureProductBySlug(
  slug: string,
  channel: string | null
): Promise<VendureProductDetail | null> {
  const query = `
    query GetProductBySlug($slug: String!) {
      product(slug: $slug) {
        id
        name
        slug
        description
        productSpecification {
          id
          allergens
          calories
          protein
          carbohydrates
          fat
          ingredients
          isVegetarian
          isVegan
          isGlutenFree
          isLactoseFree
          isProductSpecDisplay
          notes
          items {
            id
            type
            value
            referencedProduct {
              id
              name
              slug
              featuredAsset {
                preview
              }
            }
          }
        }
        featuredAsset {
          id
          preview
        }
        assets {
          id
          preview
        }
        facetValues {
          id
          name
          code
          facet {
            id
            name
            code
          }
        }
        variants {
          id
          name
          sku
          price
          priceWithTax
          currencyCode
          stockLevel
          featuredAsset {
            id
            preview
          }
          assets {
            id
            preview
          }
          options {
            id
            code
            name
            groupId
            group {
              id
              code
              name
            }
          }
        }
      }
    }
  `;

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "vendure-token": channel || defaultChannel, // 🔑 CHANNEL
    };

    const response = await fetch(VENDURE_API_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        query,
        variables: { slug },
      }),
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error(`Vendure API error: ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
      console.error("GraphQL errors:", result.errors);
      throw new Error("Failed to fetch product from Vendure");
    }

    return result.data?.product || null;
  } catch (error) {
    console.error("Error fetching Vendure product by slug:", error);
    throw error;
  }
}
